import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Table,
  Space,
  Input,
  Drawer,
  Button,
  Select,
  Tooltip,
  TablePaginationConfig,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
  FilterFilled,
  FilterOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import { PageHeader } from "../../../components/page-header/page-header";
import useFetch from "../../../hooks/useFetch";
import { GetTelemetryDataList } from "../../../utils/api.constant";
import {
  eTelemetryProductType,
  eResultCode,
  eDATE_FORMAT,
  eNumberFilterOperator,
  eStringFilterOperator,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { UserContext } from "../../../context";
import { TContext } from "../../../types/config";
import { TTelemetryData, TTelemetryColumnFilter } from "../../../types";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import styles from "./telemetry.module.css";

const TELEMETRY_COLUMN_KEY = "telemetryList";

const numberColumns = [
  "PD",
  "PWR",
  "SCFM",
  "TA",
  "ST",
  "AV",
  "RV",
  "RPM",
  "A",
  "RV1",
  "AV1",
  "PG",
  "PressDecay",
  "Pressure",
  "AMPS",
  "W",
  "DT",
];

const safeParseNumber = (val: any): number | undefined => {
  if (val === null || val === undefined || val === "") return undefined;
  const num = Number(val);
  return isNaN(num) ? undefined : num;
};

const telemetryTabs = [
  { label: "Rook2", value: eTelemetryProductType.ROOK2 },
  { label: "Falcon", value: eTelemetryProductType.FALCON },
  { label: "MiniAKB", value: eTelemetryProductType.MINIAKB },
  { label: "3BA1002", value: eTelemetryProductType.PROD3ba1002 },
  { label: "ABL", value: eTelemetryProductType.ABL },
];

const defaultFromDate = dayjs().subtract(30, "day");
const defaultToDate = dayjs();
const { RangePicker } = DatePicker;

const productNameColumn = {
  title: "Product Name",
  dataIndex: "ProductName",
  key: "ProductName",
};

const productSupportedColumns: Record<number, string[]> = {
  [eTelemetryProductType.ROOK2]: [
    "SerialNo",
    "PD",
    "PWR",
    "SCFM",
    "TA",
    "ST",
    "AV",
    "RV",
    "Location",
    "CreatedOn",
  ],
  [eTelemetryProductType.FALCON]: [
    "SerialNo",
    "RPM",
    "PWR",
    "A",
    "TA",
    "ST",
    "RV1",
    "AV1",
    "PG",
    "Location",
    "CreatedOn",
  ],
  [eTelemetryProductType.MINIAKB]: [
    "SerialNo",
    "ST",
    "PressDecay",
    "Pressure",
    "AV",
    "RV",
    "TA",
    "PWR",
    "AMPS",
    "RPM",
    "Location",
    "CreatedOn",
  ],
  [eTelemetryProductType.PROD3ba1002]: [
    "SerialNo",
    "ST",
    "COG",
    "RPM",
    "PG",
    "A",
    "W",
    "AV",
    "RV",
    "type",
    "Location",
    "CreatedOn",
  ],
  [eTelemetryProductType.ABL]: [
    "SerialNo",
    "ST",
    "RPM",
    "PWR",
    "DT",
    "RV",
    "Location",
    "CreatedOn",
  ],
};

export default function TelemetryList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const context: TContext = useContext(UserContext);

  // Table Data & Pagination States
  const [data, setData] = useState<TTelemetryData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Sorting & Search States
  const [orderBy, setOrderBy] = useState("");
  const [orderType, setOrderType] = useState("");
  const [globalsearchText, setGlobalSearchText] = useState("");

  // Tab State
  const [productTypeId, setProductTypeId] = useState<number>(
    eTelemetryProductType.ROOK2,
  );

  const selectedProductName =
    telemetryTabs.find((tab) => tab.value === productTypeId)?.label || "";

  const [filterParams, setFilterParams] = useState({
    fromDate: defaultFromDate.format("YYYY-MM-DD"),
    toDate: defaultToDate.format("YYYY-MM-DD"),
  });

  // Filters State
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  // Column management states
  const [columns, setColumns] = useState<any[]>([]);
  const [initialColumns, setInitialColumns] = useState<any[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState<any[]>([]);

  // Mapping function to align client PascalCase dataIndex with API expected database column names
  const mapColumnKeyToApiName = (key: string): string => {
    switch (key) {
      case "RV1":
        return "rv1";
      case "AV1":
        return "av1";
      case "PressDecay":
        return "pressDecay";
      case "ProductName":
        return "productName";
      case "SerialNo":
        return "serialNo";
      case "CreatedOn":
        return "createdon";
      default:
        return key.toLowerCase();
    }
  };

  // Custom filter dropdown UI for headers
  const getCustomFilterDropdown = (
    dataIndex: string,
    type: "number" | "string",
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => {
      const currentVal =
        selectedKeys[0] ||
        (type === "number"
          ? {
              filterOperator: eNumberFilterOperator.LESS_THAN_OR_EQUAL,
              filterValue: "",
              minValue: undefined,
              maxValue: undefined,
            }
          : {
              filterOperator: eStringFilterOperator.CONTAINS,
              filterValue: "",
            });

      const handleOperatorChange = (opVal: number) => {
        setSelectedKeys([{ ...currentVal, filterOperator: opVal }]);
      };

      const handleValueChange = (val: string) => {
        setSelectedKeys([{ ...currentVal, filterValue: val }]);
      };

      const handleMinChange = (val: string) => {
        setSelectedKeys([
          { ...currentVal, minValue: val === "" ? undefined : Number(val) },
        ]);
      };

      const handleMaxChange = (val: string) => {
        setSelectedKeys([
          { ...currentVal, maxValue: val === "" ? undefined : Number(val) },
        ]);
      };

      const stringOperators = [
        { label: "Contains", value: eStringFilterOperator.CONTAINS },
        { label: "Starts With", value: eStringFilterOperator.STARTS_WITH },
        { label: "Ends With", value: eStringFilterOperator.ENDS_WITH },
        { label: "Equals", value: eStringFilterOperator.EQUALS },
        { label: "Not Contains", value: eStringFilterOperator.NOT_CONTAINS },
        { label: "Not Equals", value: eStringFilterOperator.NOT_EQUALS },
      ];

      const numberOperators = [
        { label: ">", value: eNumberFilterOperator.GREATER_THAN },
        { label: ">=", value: eNumberFilterOperator.GREATER_THAN_OR_EQUAL },
        { label: "<", value: eNumberFilterOperator.LESS_THAN },
        { label: "<=", value: eNumberFilterOperator.LESS_THAN_OR_EQUAL },
        { label: "=", value: eNumberFilterOperator.EQUAL },
        { label: "!=", value: eNumberFilterOperator.NOT_EQUAL },
        { label: "Between", value: eNumberFilterOperator.BETWEEN },
      ];

      const operators = type === "number" ? numberOperators : stringOperators;

      return (
        <div
          className={styles.filterDropdownContainer}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div>
            <div className={styles.filterLabel}>Operator</div>
            <Select
              className={styles.operatorSelect}
              value={currentVal.filterOperator}
              onChange={handleOperatorChange}
              options={operators as any}
              size="small"
            />
          </div>

          {type === "number" &&
          currentVal.filterOperator === eNumberFilterOperator.BETWEEN ? (
            <div>
              <div className={styles.filterLabel}>Range Values</div>
              <div className={styles.rangeInputContainer}>
                <Input
                  className={styles.rangeInput}
                  placeholder="Min"
                  value={
                    currentVal.minValue !== undefined ? currentVal.minValue : ""
                  }
                  onChange={(e) => handleMinChange(e.target.value)}
                  type="number"
                  size="small"
                />
                <span>-</span>
                <Input
                  className={styles.rangeInput}
                  placeholder="Max"
                  value={
                    currentVal.maxValue !== undefined ? currentVal.maxValue : ""
                  }
                  onChange={(e) => handleMaxChange(e.target.value)}
                  type="number"
                  size="small"
                />
              </div>
            </div>
          ) : (
            <div>
              <div className={styles.filterLabel}>Value</div>
              <Input
                className={styles.filterInput}
                placeholder="Value"
                value={currentVal.filterValue || ""}
                onChange={(e) => handleValueChange(e.target.value)}
                type={type === "number" ? "number" : "text"}
                size="small"
              />
            </div>
          )}

          <div className={styles.filterActionRow}>
            <Button
              type="link"
              onClick={() => {
                if (clearFilters) {
                  clearFilters();
                }
              }}
              size="small"
              className={styles.filterResetBtn}
            >
              Reset
            </Button>
            <Button
              type="primary"
              onClick={() => {
                confirm();
              }}
              size="small"
              className={styles.filterSubmitBtn}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
  });

  const getInitialColumns = (): any[] => [
    {
      title: "Serial Number",
      dataIndex: "SerialNo",
      key: "SerialNo",
      sorter: true,
      ...getCustomFilterDropdown("SerialNo", "string"),
    },
    {
      title: "Location",
      dataIndex: "Location",
      key: "Location",
      sorter: true,
      ...getCustomFilterDropdown("Location", "string"),
    },
    {
      title: "Created On",
      dataIndex: "CreatedOn",
      key: "CreatedOn",
      sorter: true,
      render: (CreatedOn: string) => {
        return CreatedOn
          ? DateUtils.format(CreatedOn, context?.user?.dateFormat, true)
          : "-";
      },
    },
    {
      title: "Discharge Pressure",
      dataIndex: "PD",
      key: "PD",
      sorter: true,
      ...getCustomFilterDropdown("PD", "number"),
    },
    {
      title: "Power",
      dataIndex: "PWR",
      key: "PWR",
      sorter: true,
      ...getCustomFilterDropdown("PWR", "number"),
    },
    {
      title: "Flow rate SCFM",
      dataIndex: "SCFM",
      key: "SCFM",
      sorter: true,
      ...getCustomFilterDropdown("SCFM", "number"),
    },
    {
      title: "Temperature",
      dataIndex: "TA",
      key: "TA",
      sorter: true,
      ...getCustomFilterDropdown("TA", "number"),
    },
    {
      title: "Station Number",
      dataIndex: "ST",
      key: "ST",
      sorter: true,
      ...getCustomFilterDropdown("ST", "number"),
    },
    {
      title: "Axial Vibration",
      dataIndex: "AV",
      key: "AV",
      sorter: true,
      ...getCustomFilterDropdown("AV", "number"),
    },
    {
      title: "Radial Vibration",
      dataIndex: "RV",
      key: "RV",
      sorter: true,
      ...getCustomFilterDropdown("RV", "number"),
    },

    {
      title: "Motor Speed",
      dataIndex: "RPM",
      key: "RPM",
      sorter: true,
      ...getCustomFilterDropdown("RPM", "number"),
    },
    {
      title: "Current",
      dataIndex: "A",
      key: "A",
      sorter: true,
      ...getCustomFilterDropdown("A", "number"),
    },
    {
      title: "Radial Vibration",
      dataIndex: "RV1",
      key: "RV1",
      sorter: true,
      ...getCustomFilterDropdown("RV1", "number"),
    },
    {
      title: "Axial Vibration",
      dataIndex: "AV1",
      key: "AV1",
      sorter: true,
      ...getCustomFilterDropdown("AV1", "number"),
    },
    {
      title: "Discharge Pressure",
      dataIndex: "PG",
      key: "PG",
      sorter: true,
      ...getCustomFilterDropdown("PG", "number"),
    },
    {
      title: "Decay Pressure",
      dataIndex: "PressDecay",
      key: "PressDecay",
      sorter: true,
      ...getCustomFilterDropdown("PressDecay", "number"),
    },
    {
      title: "Pressure",
      dataIndex: "Pressure",
      key: "Pressure",
      sorter: true,
      ...getCustomFilterDropdown("Pressure", "number"),
    },
    {
      title: "Current",
      dataIndex: "AMPS",
      key: "AMPS",
      sorter: true,
      ...getCustomFilterDropdown("AMPS", "number"),
    },
    {
      title: "Cog Test Result",
      dataIndex: "COG",
      key: "COG",
      sorter: true,
      ...getCustomFilterDropdown("COG", "string"),
    },
    {
      title: "Blower Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      ...getCustomFilterDropdown("type", "string"),
    },
    {
      title: "Power",
      dataIndex: "W",
      key: "W",
      sorter: true,
      ...getCustomFilterDropdown("W", "number"),
    },
    {
      title: "Discharge Temp.",
      dataIndex: "DT",
      key: "DT",
      sorter: true,
      ...getCustomFilterDropdown("DT", "number"),
    },
  ];

  // Set up column configs and local preference loading based on active product type
  useEffect(() => {
    const allCols = getInitialColumns();
    const supportedKeys = productSupportedColumns[productTypeId] || [];
    const filteredCols = allCols.filter((col) =>
      supportedKeys.includes(col.key),
    );
    setInitialColumns(filteredCols);

    (async () => {
      const prefsKey = `${TELEMETRY_COLUMN_KEY}_${productTypeId}`;
      const savedPreferences = await loadColumnPreferences(prefsKey);
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          filteredCols.map((col) => [col.key, col]),
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        // Include any new/missing columns that exist in filteredCols but weren't in savedPreferences
        const savedKeys = new Set(reorderedColumns.map((col: any) => col.key));
        const missingColumns = filteredCols.filter(
          (col) => !savedKeys.has(col.key),
        );
        const finalColumns = [...reorderedColumns, ...missingColumns];

        setColumns(finalColumns);
        setDraftColumns(finalColumns);
      } else {
        setColumns(filteredCols);
        setDraftColumns(filteredCols);
      }
    })();
  }, [context?.user, productTypeId]);

  // Reactive visible columns computing
  const visibleColumns = useMemo(() => {
    return columns
      .filter((col) => !col.hidden)
      .map((col) => {
        const dataIndex = col.dataIndex as string;
        return {
          ...col,
          filteredValue: activeFilters[dataIndex]
            ? activeFilters[dataIndex]
            : null,
          sortOrder:
            orderBy === mapColumnKeyToApiName(dataIndex)
              ? orderType === "A"
                ? "ascend"
                : orderType === "D"
                  ? "descend"
                  : null
              : null,
        };
      });
  }, [columns, activeFilters, orderBy, orderType]);

  // Helper to build column filters payload
  const buildColumnFilters = (filters: Record<string, any>) => {
    const newColumnFilters: TTelemetryColumnFilter[] = [];
    Object.entries(filters).forEach(([key, val]) => {
      const filterObj = val?.[0];
      if (filterObj) {
        const isNumber = numberColumns.includes(key);
        const isBetween =
          isNumber &&
          filterObj.filterOperator === eNumberFilterOperator.BETWEEN;
        const hasValue = isBetween
          ? filterObj.minValue !== undefined || filterObj.maxValue !== undefined
          : filterObj.filterValue !== undefined && filterObj.filterValue !== "";

        if (hasValue) {
          newColumnFilters.push({
            columnName: mapColumnKeyToApiName(key),
            dataType: isNumber ? "number" : "string",
            filterOperator: filterObj.filterOperator,
            filterValue: isBetween
              ? undefined
              : isNumber
                ? Number(filterObj.filterValue)
                : String(filterObj.filterValue),
            minValue: isBetween
              ? filterObj.minValue !== undefined
                ? Number(filterObj.minValue)
                : undefined
              : undefined,
            maxValue: isBetween
              ? filterObj.maxValue !== undefined
                ? Number(filterObj.maxValue)
                : undefined
              : undefined,
          });
        }
      }
    });
    return newColumnFilters;
  };

  // Main Fetch logic
  const fetchData = async () => {
    try {
      setIsLoading(true);

      const requestPayload = {
        data: {
          currentPage,
          pageSize,
          searchText: globalsearchText,
          orderBy,
          orderType,
          productTypeId,
          fromDate: filterParams.fromDate,
          toDate: filterParams.toDate,
          columnFilters: buildColumnFilters(activeFilters),
        },
      };

      const response = await post(GetTelemetryDataList, requestPayload);
      if (response) {
        const { data, dataResponse, filterModel } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const formattedData = data.map((item: any, index: number) => ({
            Id: safeParseNumber(item.Id ?? item.id),
            ProductType: safeParseNumber(
              item.ProductType ?? item.productType ?? item.product_type,
            ),
            ProductName:
              telemetryTabs.find(
                (tab) =>
                  tab.value ===
                  safeParseNumber(
                    item.ProductType ??
                      item.productType ??
                      item.product_type ??
                      productTypeId,
                  ),
              )?.label ??
              item.ProductName ??
              item.productName ??
              item.product_name ??
              "-",
            SerialNo: item.serialNo,
            PD: safeParseNumber(item.PD ?? item.pd),
            PWR: safeParseNumber(item.PWR ?? item.pwr),
            SCFM: safeParseNumber(item.SCFM ?? item.scfm),
            TA: safeParseNumber(item.TA ?? item.ta),
            ST: safeParseNumber(item.ST ?? item.st),
            AV: safeParseNumber(item.AV ?? item.av),
            RV: safeParseNumber(item.RV ?? item.rv),
            Location: item.Location ?? item.location,
            RPM: safeParseNumber(item.RPM ?? item.rpm),
            A: safeParseNumber(item.A ?? item.a),
            RV1: safeParseNumber(item.RV1 ?? item.rv1 ?? item.rV1),
            AV1: safeParseNumber(item.AV1 ?? item.av1 ?? item.aV1),
            PG: safeParseNumber(item.PG ?? item.pg),
            PressDecay: safeParseNumber(
              item.PressDecay ?? item.pressDecay ?? item.press_decay,
            ),
            Pressure: safeParseNumber(item.Pressure ?? item.pressure),
            AMPS: safeParseNumber(item.AMPS ?? item.amps),
            COG: item.COG ?? item.cog,
            type: item.type ?? item.type,
            W: safeParseNumber(item.W ?? item.w),
            CreatedOn: item.CreatedOn ?? item.createdOn ?? item.createdon,
            key: (item.Id ?? item.id ?? index).toString(),
            srNo: (currentPage - 1) * pageSize + index + 1,
            DT: safeParseNumber(item?.dt),
            ...item,
          }));
          setData(formattedData);
          setTotalRows(
            // filterModel?.totalRows ||
            filterModel?.filterRowsCount,
          );
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error("Telemetry fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch telemetry records for excel downloads asynchronously
  const handleFetchExportData = async (
    exportType: "all" | "filtered",
  ): Promise<any[]> => {
    try {
      let requestPayload;
      if (exportType === "all") {
        requestPayload = {
          data: {
            currentPage: 1,
            pageSize: 999999999,
            searchText: "",
            orderBy: "",
            orderType: "",
            productTypeId,
            fromDate: filterParams.fromDate,
            toDate: filterParams.toDate,
            columnFilters: [],
          },
        };
      } else {
        requestPayload = {
          data: {
            currentPage: 1,
            pageSize: 999999999,
            searchText: globalsearchText,
            orderBy,
            orderType,
            productTypeId,
            fromDate: filterParams.fromDate,
            toDate: filterParams.toDate,
            columnFilters: buildColumnFilters(activeFilters),
          },
        };
      }

      const response = await post(GetTelemetryDataList, requestPayload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const formattedExportData = data.map((item: any, index: number) => ({
            Id: safeParseNumber(item.Id ?? item.id),
            ProductType: safeParseNumber(
              item.ProductType ?? item.productType ?? item.product_type,
            ),
            ProductName:
              telemetryTabs.find(
                (tab) =>
                  tab.value ===
                  safeParseNumber(
                    item.ProductType ??
                      item.productType ??
                      item.product_type ??
                      productTypeId,
                  ),
              )?.label ??
              item.ProductName ??
              item.productName ??
              item.product_name ??
              "-",
            SerialNo: item.serialNo,
            PD: safeParseNumber(item.PD ?? item.pd),
            PWR: safeParseNumber(item.PWR ?? item.pwr),
            SCFM: safeParseNumber(item.SCFM ?? item.scfm),
            TA: safeParseNumber(item.TA ?? item.ta),
            ST: safeParseNumber(item.ST ?? item.st),
            AV: safeParseNumber(item.AV ?? item.av),
            RV: safeParseNumber(item.RV ?? item.rv),
            Location: item.Location ?? item.location,
            RPM: safeParseNumber(item.RPM ?? item.rpm),
            A: safeParseNumber(item.A ?? item.a),
            RV1: safeParseNumber(item.RV1 ?? item.rv1 ?? item.rV1),
            AV1: safeParseNumber(item.AV1 ?? item.av1 ?? item.aV1),
            PG: safeParseNumber(item.PG ?? item.pg),
            PressDecay: safeParseNumber(
              item.PressDecay ?? item.pressDecay ?? item.press_decay,
            ),
            Pressure: safeParseNumber(item.Pressure ?? item.pressure),
            AMPS: safeParseNumber(item.AMPS ?? item.amps),
            COG: item.COG ?? item.cog,
            type: item.type ?? item.type,
            W: safeParseNumber(item.W ?? item.w),
            CreatedOn: item.CreatedOn ?? item.createdOn ?? item.createdon,
            key: (item.Id ?? item.id ?? index).toString(),
            srNo: index + 1,
            DT: safeParseNumber(item?.dt),
            ...item,
          }));

          return formattedExportData.map(formatExportRow);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error("Telemetry export fetch error:", error);
    }
    return [];
  };

  // Re-fetch on payload dependencies change
  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    pageSize,
    orderBy,
    orderType,
    productTypeId,
    activeFilters,
    globalsearchText,
    filterParams.fromDate,
    filterParams.toDate,
  ]);

  // Table Change handler (pagination, filters, sorting)
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);

    if (sorter && sorter.field) {
      setOrderBy(mapColumnKeyToApiName(sorter.field));
      setOrderType(
        sorter.order === "ascend" ? "A" : sorter.order === "descend" ? "D" : "",
      );
    } else {
      setOrderBy("");
      setOrderType("");
    }

    setActiveFilters(filters);
  };

  // Reset all filters and sorting
  const handleResetAll = () => {
    setActiveFilters({});
    setOrderBy("");
    setOrderType("");
    setGlobalSearchText("");
    setFilterParams({
      fromDate: defaultFromDate.format("YYYY-MM-DD"),
      toDate: defaultToDate.format("YYYY-MM-DD"),
    });
    setCurrentPage(1);
  };

  // Drag-and-drop column sorting
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  // Toggle column visibility
  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraft) =>
      prevDraft.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  // Reset columns visibility to default
  const handleResetColumns = () => {
    setDraftColumns(initialColumns);
    const prefsKey = `${TELEMETRY_COLUMN_KEY}_${productTypeId}`;
    clearColumnPreferences(prefsKey);
  };

  // Save customized column preferences
  const handleSaveColumns = () => {
    setColumns(draftColumns);
    const prefsKey = `${TELEMETRY_COLUMN_KEY}_${productTypeId}`;
    saveColumnPreferences(prefsKey, draftColumns);
    setDrawerVisible(false);
  };

  // Mapped formatting row for excel downloads
  const formatExportRow = (item: TTelemetryData) => {
    const mapped = {
      ...item,
      CreatedOn: item?.CreatedOn
        ? DateUtils.formatForExport(
            item?.CreatedOn,
            true,
            context?.user?.dateFormat as eDATE_FORMAT,
          )
        : "-",
    };

    const completeRow: Record<string, any> = {};
    const keysToExport = [
      "ProductName",
      "SerialNo",
      "PD",
      "PWR",
      "SCFM",
      "TA",
      "ST",
      "AV",
      "RV",
      "Location",
      "RPM",
      "A",
      "RV1",
      "AV1",
      "PG",
      "PressDecay",
      "Pressure",
      "AMPS",
      "COG",
      "type",
      "W",
      "DT",
      "CreatedOn",
    ];

    keysToExport.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null || val === undefined || val === "" ? "-" : val;
    });

    return completeRow;
  };

  const exportData = data.map(formatExportRow);

  return (
    <>
      <div className={styles.parentContainer}>
        {telemetryTabs.map((tab) => (
          <div
            key={tab.value}
            className={`${styles.tab} ${
              productTypeId === tab.value ? styles.activeTab : ""
            }`}
            onClick={() => {
              setProductTypeId(tab.value);
              setCurrentPage(1);
            }}
          >
            <span
              className={
                productTypeId === tab.value
                  ? styles.activeText
                  : styles.inactiveText
              }
            >
              {tab.label}
            </span>
          </div>
        ))}
      </div>

      <div className="pageBody">
        <PageHeader title={`${totalRows} Total Telemetry Entries`} size="large">
          <Space>
            <RangePicker
              format={context?.user?.dateFormat?.toUpperCase()}
              size={"small"}
              allowClear={false}
              value={[dayjs(filterParams.fromDate), dayjs(filterParams.toDate)]}
              onChange={(dates) => {
                if (dates) {
                  setFilterParams((prev) => ({
                    ...prev,
                    fromDate: dates[0] ? dates[0].format("YYYY-MM-DD") : "",
                    toDate: dates[1] ? dates[1].format("YYYY-MM-DD") : "",
                  }));
                }
              }}
            />
            <Input
              placeholder="Search keyword"
              value={globalsearchText}
              onChange={(e) => {
                setGlobalSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
              prefix={<SearchOutlined />}
            />

            <ExportToCSV
              allColumns={[productNameColumn, ...initialColumns]}
              data={exportData}
              filteredData={exportData}
              visibleColumns={[productNameColumn, ...visibleColumns]}
              moduleName={`${selectedProductName}_Testbench_Data`}
              fetchExportData={handleFetchExportData}
            />
            <Tooltip title="Reset Filters">
              <FilterOutlined
                onClick={handleResetAll}
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
            <Tooltip title="Configure Columns">
              <OrderedListOutlined
                onClick={() => {
                  setDraftColumns(columns);
                  setDrawerVisible(true);
                }}
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          </Space>
        </PageHeader>

        <div className="tableWrap">
          <Table
            columns={visibleColumns}
            dataSource={data}
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRows,
              position: ["bottomLeft"],
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100", "250"],
              showTotal: (total: any, range: any[]) =>
                `Showing ${range[0]}-${range[1]} of ${total} total entries`,
            }}
            onChange={handleTableChange}
            bordered={false}
            size="small"
            scroll={{ x: "max-content" }}
          />
        </div>

        <Drawer
          title="Customize Columns"
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          style={{ transition: "transform 0.3s ease-in-out" }}
        >
          <ColumnManager
            columns={draftColumns}
            initialColumns={initialColumns}
            onDragEnd={handleDragEnd}
            onToggleColumn={toggleColumn}
            onResetColumns={handleResetColumns}
            onSave={handleSaveColumns}
            onCancel={() => setDrawerVisible(false)}
          />
        </Drawer>
      </div>
    </>
  );
}
